import * as React from 'react';
import { Settings } from 'lucide-react';
import { PostureStrip, ScoreBar } from '@/components/ui/charts';
import type { PostureStatus } from '@/components/ui/posture-chip';
import { cn } from '@/lib/utils/cn';
import type { HealthReadinessView, HrCriterion, HrDomain } from '@/lib/api/health-readiness';
import { formatDayMonth } from '@/lib/utils/format';
import { POSTURE_LABEL, formatPercent, formatWeight } from './model';
import { formatPoints } from './overview/overview-model';
import { AiMarker } from './detail-parts';

/**
 * The lists on the left of the Domains and Go-live criteria tabs (T-0243 /
 * E22-F02; prototype `domainsTab()` and `criteriaTab()` lists).
 *
 * Domains: a posture bar, the name, "Amber · rated 7 Oct · 2 open", and on
 * the right the AI marker when a reading waits and the rating at each
 * snapshot (`PostureStrip`). Criteria: grouped by category with the
 * category's points (the API's group values), each row with its weight, a
 * mini score bar against the gates and the score. One direction: a row
 * selects; the detail on the right follows. Leads get the manage row at
 * the foot.
 */

const ROW_FOCUS =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600';

/** The list's frame: a paper panel, sticky beside the detail. */
export const LIST_FRAME =
  'flex flex-col gap-px rounded-card border border-ink-100 bg-paper-0 p-1.5 lg:sticky lg:top-[72px] lg:max-h-[calc(100vh-88px)] lg:overflow-auto';

const rowClass = (active: boolean) =>
  cn(
    'w-full rounded-[6px] border text-left text-ink-900',
    ROW_FOCUS,
    active
      ? 'border-accent-amber-600 bg-paper-0 shadow-[inset_0_0_0_1px_var(--accent-amber-600)]'
      : 'border-transparent hover:bg-paper-50',
  );

const BAR: Record<PostureStatus, string> = {
  healthy: 'bg-posture-healthy',
  'at-risk': 'bg-posture-at-risk',
  critical: 'bg-posture-critical',
  unassessed: 'bg-ink-200',
};

export interface DomainListProps {
  view: HealthReadinessView;
  /** The selected domain's id. */
  selected: string | null;
  onSelect: (domainId: string) => void;
  /** Leads see "Manage domains". */
  canManage: boolean;
  manageActive: boolean;
  onManage: () => void;
}

export function DomainList({
  view,
  selected,
  onSelect,
  canManage,
  manageActive,
  onManage,
}: DomainListProps) {
  return (
    <nav aria-label="Domains" className="flex flex-col gap-px">
      {view.domains.map((d) => (
        <DomainRow
          key={d.id}
          domain={d}
          active={!manageActive && selected === d.id}
          onClick={() => onSelect(d.id)}
        />
      ))}
      {canManage ? (
        <ManageRow
          label="Manage domains"
          meta={
            view.retiredDomains.length > 0 ? `${view.retiredDomains.length} retired` : undefined
          }
          active={manageActive}
          onClick={onManage}
        />
      ) : null}
    </nav>
  );
}

export interface CriteriaListProps {
  view: HealthReadinessView;
  selected: string | null;
  onSelect: (criterionId: string) => void;
  canManage: boolean;
  manageActive: boolean;
  onManage: () => void;
}

export function CriteriaList({
  view,
  selected,
  onSelect,
  canManage,
  manageActive,
  onManage,
}: CriteriaListProps) {
  const readiness = view.verdict.readiness;
  if (!readiness) return null;
  return (
    <nav aria-label="Go-live criteria" className="flex flex-col gap-px">
      {view.criteriaGroups.map((group) => (
        <React.Fragment key={group.category}>
          <div className="flex items-center gap-2 px-2 pb-1.5 pt-3 text-[11px] font-semibold uppercase leading-4 tracking-[0.06em] text-ink-500">
            <span className="min-w-0 truncate">{group.category}</span>
            {group.weight !== null ? (
              <span className="ml-auto whitespace-nowrap font-mono font-medium normal-case tracking-normal">
                {(group.aggregatePercent ?? 0).toFixed(1)} / {formatPoints(group.weight * 100)} pts
              </span>
            ) : null}
          </div>
          {group.criteria.map((c) => (
            <CriterionRow
              key={c.id}
              criterion={c}
              thresholds={readiness.thresholds}
              active={!manageActive && selected === c.id}
              onClick={() => onSelect(c.id)}
            />
          ))}
        </React.Fragment>
      ))}
      {canManage ? (
        <ManageRow label="Manage criteria and weights" active={manageActive} onClick={onManage} />
      ) : null}
    </nav>
  );
}

function DomainRow({
  domain,
  active,
  onClick,
}: {
  domain: HrDomain;
  active: boolean;
  onClick: () => void;
}) {
  const meta = `${
    domain.ratedAt
      ? `${POSTURE_LABEL[domain.status]} · rated ${formatDayMonth(domain.ratedAt)}`
      : 'Not rated'
  }${domain.openFindings > 0 ? ` · ${domain.openFindings} open` : ''}`;
  return (
    <button
      type="button"
      aria-current={active ? 'true' : undefined}
      onClick={onClick}
      className={cn(
        rowClass(active),
        'grid grid-cols-[4px_1fr_auto] items-center gap-2.5 py-2 pl-1.5 pr-2',
      )}
    >
      <span aria-hidden="true" className={cn('self-stretch rounded-[2px]', BAR[domain.status])} />
      <span className="min-w-0">
        <span className="block text-body-sm font-medium leading-[18px]">{domain.name}</span>
        <span className="block text-[11px] leading-4 text-ink-500">{meta}</span>
      </span>
      <span className="flex flex-col items-end gap-1">
        {domain.aiProposal ? <AiMarker /> : null}
        {domain.history.length > 0 ? (
          <PostureStrip
            size="sm"
            label={domain.name}
            current={domain.status}
            cells={domain.history.map((h) => ({ date: h.capturedAt, posture: h.status }))}
            emptyText={null}
          />
        ) : null}
      </span>
    </button>
  );
}

function CriterionRow({
  criterion,
  thresholds,
  active,
  onClick,
}: {
  criterion: HrCriterion;
  thresholds: { go: number; conditional: number };
  active: boolean;
  onClick: () => void;
}) {
  const scored = criterion.scorePercent !== null;
  const meta = [
    `weight ${formatWeight(criterion.weight)}`,
    scored ? null : 'needs a score',
    criterion.aiCandidate ? 'AI reading waiting' : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <button
      type="button"
      aria-current={active ? 'true' : undefined}
      onClick={onClick}
      className={cn(rowClass(active), 'grid grid-cols-[1fr_72px_36px] items-center gap-2.5 p-2')}
    >
      <span className="min-w-0">
        <span className="block text-body-sm font-medium leading-[18px]">{criterion.name}</span>
        <span className="block text-[11px] leading-4 text-ink-500">{meta}</span>
      </span>
      <ScoreBar value={criterion.scorePercent} thresholds={thresholds} label={criterion.name} />
      <span
        className={cn(
          'text-right font-mono text-meta font-semibold',
          scored ? 'text-ink-900' : 'text-ink-300',
        )}
      >
        {formatPercent(criterion.scorePercent)}
      </span>
    </button>
  );
}

function ManageRow({
  label,
  meta,
  active,
  onClick,
}: {
  label: string;
  meta?: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-current={active ? 'true' : undefined}
      onClick={onClick}
      className={cn(
        'mt-1 flex w-full items-center gap-2 border-t border-ink-100 px-2 py-2.5 text-left text-meta font-medium',
        ROW_FOCUS,
        active ? 'text-ink-900' : 'text-ink-500 hover:text-ink-900',
      )}
    >
      <Settings className="h-3 w-3 flex-none" aria-hidden="true" />
      <span className="min-w-0 flex-1">{label}</span>
      {meta ? <span className="text-[11px] font-normal text-ink-500">{meta}</span> : null}
    </button>
  );
}
